import React from 'react'
import { usePlayerStore } from '../store/player-store'
import { useUIStore } from '../store/ui-store'
import { useGameStore } from '../store/game-store'
import MissionPopup from '../game/MissionPopup'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

interface LobbyAction {
  icon: string
  label: string
  screen: string
  description: string
  action?: () => Promise<void>
}

export default function LobbyScreen(): React.ReactElement {
  const { profile, token, loginGuest, isLoading, logout } = usePlayerStore()
  const { navigateTo, showToast } = useUIStore()
  const { reset, isConnected } = useGameStore()
  const [joining, setJoining] = React.useState(false)

  const handleQuickPlay = async (): Promise<void> => {
    if (joining) return
    console.log('[Lobby] Quick Play clicked')
    setJoining(true)
    try {
      reset()
      const tk = localStorage.getItem('poker_token')
      if (!tk) {
        showToast('Please login first', 'error')
        setJoining(false)
        return
      }
      const res = await fetch(`${API_BASE}/matchmaking/quick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ tableSize: 6, stakeLevel: 'micro' }),
      })
      const json = await res.json()
      if (!json.success) {
        showToast(json.error || 'Failed to join game', 'error')
        setJoining(false)
        return
      }
      useGameStore.getState().connectToTable(json.data.tableId, tk, profile?.name || 'Player', tk, 6, 'micro')
      setJoining(false)
      navigateTo('table')
      return
    } catch (e) {
      console.error('[Lobby] Quick play error:', e)
      showToast('Connection error. Try again.', 'error')
    }
    setJoining(false)
  }

  const handleSinglePlayer = async (): Promise<void> => {
    const tk = localStorage.getItem('poker_token') || token
    if (!tk) {
      showToast('Please login first', 'error')
      return
    }
    reset()
    useGameStore.getState().connectToTable('ai-game', tk, profile?.name || 'Player', tk, 2, 'micro')
    navigateTo('table')
  }

  const handleRetryLogin = (): void => {
    loginGuest()
  }

  const ACTIONS: LobbyAction[] = [
    { icon: '♠', label: 'Quick Play', screen: 'table', description: 'Join a cash game now', action: handleQuickPlay },
    { icon: '🪑', label: 'Tables', screen: 'tables', description: 'Browse & create tables' },
    { icon: '🏆', label: 'Tournaments', screen: 'table', description: 'Sit & Go + MTT' },
    { icon: '🛒', label: 'Shop', screen: 'shop', description: 'Buy chips & skins' },
    { icon: '📋', label: 'Missions', screen: 'missions', description: 'Daily challenges' },
    { icon: '🏅', label: 'Leaderboard', screen: 'leaderboard', description: 'Top players' },
    { icon: '👥', label: 'Clubs', screen: 'clubs', description: 'Play with friends' },
    { icon: '🤝', label: 'Friends', screen: 'friends', description: 'Your contacts' },
    { icon: '👤', label: 'Profile', screen: 'profile', description: 'Your stats' },
    { icon: '🎮', label: 'Single Player', screen: 'table', description: 'Practice vs AI', action: handleSinglePlayer },
  ]

  const handleAction = (action: LobbyAction): void => {
    if (action.action) {
      action.action()
    } else {
      navigateTo(action.screen as any)
    }
  }

  return (
    <div className="lobby">
      <div className="lobby-header">
        <div>
          <div className="lobby-title">POKER CLUB</div>
          {profile ? (
            <div style={{ fontSize: 12, color: '#888' }}>
              Lv.{profile.level} · {profile.name}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#888' }}>
              {isLoading ? 'Logging in...' : 'Not logged in'}
            </div>
          )}
        </div>
        <div>
          <div className="lobby-chip-balance">
            🪙 {profile ? profile.chips.toLocaleString() : isLoading ? '...' : '0'}
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 4, justifyContent: 'flex-end' }}>
            {profile ? (
              <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }} onClick={logout}>
                Logout
              </button>
            ) : (
              <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }} onClick={handleRetryLogin}>
                Retry Login
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="lobby-actions">
        {ACTIONS.map(action => (
          <div key={action.label} className={`lobby-card${joining && action.label === 'Quick Play' ? ' disabled' : ''}`} onClick={() => handleAction(action)}>
            <div className="lobby-card-icon">{action.icon}</div>
            <div className="lobby-card-label">{joining && action.label === 'Quick Play' ? 'Joining...' : action.label}</div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>{action.description}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 'auto', padding: '16px 0', textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: '#444' }}>
          Poker Club v1.0.0 · Free Texas Hold'em
        </div>
      </div>

      <MissionPopup />
    </div>
  )
}
